import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "./cityPricing";

const laCroixValmer: ChauffeurPage = {
  slug: "la-croix-valmer",
  title: "Chauffeur privé à La Croix-Valmer – VTC & plages",
  subtitle:
    "Chauffeur privé et VTC à La Croix-Valmer : transfert aéroport de Nice, gare de Saint-Raphaël, plages de Gigaro, vignobles et liaison Saint-Tropez.",
  keywords: [
    "chauffeur privé la croix-valmer",
    "vtc la croix-valmer",
    "chauffeur la croix-valmer",
    "transfert la croix-valmer aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à La Croix-Valmer",
    subtitle:
      "Les plages de Gigaro, les vignobles et le Cap Lardier : transfert aéroport de Nice, mise à disposition et longue distance, 7j/7.",
    keywords: [
      "VTC La Croix-Valmer",
      "chauffeur plages de Gigaro",
      "transfert aéroport Nice",
      "chauffeur golfe de Saint-Tropez",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à La Croix-Valmer (golfe de Saint-Tropez)",
    badges: [
      "Plages de Gigaro & Cap Lardier",
      "Transfert aéroport de Nice",
      "Chauffeurs bilingues",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à La Croix-Valmer",
    items: [
      {
        title: "Plages de Gigaro",
        description:
          "Accès aux plages de Gigaro et au départ du sentier du littoral : dépose et reprise sans souci de stationnement l’été.",
      },
      {
        title: "Nature préservée & vignobles",
        description:
          "Cap Lardier, criques et domaines viticoles : un même chauffeur pour rayonner sans contrainte.",
      },
      {
        title: "Transfert longue distance",
        description:
          "Depuis l’aéroport de Nice, environ 1h20 à 1h30 de route, gérées dans un confort haut de gamme.",
      },
      {
        title: "Ancrage local",
        description:
          "Basés sur la Côte d’Azur, note 5,0/5 sur 38 avis Google, chauffeurs bilingues FR/EN.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à La Croix-Valmer",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison La Croix-Valmer ⇄ aéroport de Nice Côte d’Azur avec suivi de vol ; trajet plus long qu’au départ de Cannes, calé dès la prise en charge.",
      },
      {
        title: "Gare TGV de Saint-Raphaël",
        text: "Prise en charge à la gare de Saint-Raphaël, à environ 30 à 40 minutes, comme alternative au train.",
      },
      {
        title: "Navettes plages de Gigaro",
        text: "Vers les plages de Gigaro et le Cap Lardier, en aller-retour, sans contrainte de stationnement.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée pour les plages, les vignobles et Saint-Tropez.",
      },
      {
        title: "Longue distance",
        text: "Trajets toute distance depuis La Croix-Valmer : Cannes, Nice, Monaco et au-delà, avec un chauffeur dédié.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Plages de Gigaro",
        description:
          "Plages et criques face aux îles, cœur balnéaire de la commune.",
      },
      {
        name: "Cap Lardier",
        description: "Espace naturel protégé et sentier du littoral.",
      },
      {
        name: "Domaines viticoles",
        description: "Vignobles des Côtes de Provence autour du village.",
      },
      {
        name: "Village de La Croix-Valmer",
        description: "Centre du village et belvédères sur le golfe.",
      },
      {
        name: "Gare de Saint-Raphaël",
        description: "Gare TGV à environ 30-40 min, point de transfert.",
      },
      {
        name: "Aéroport de Nice",
        description: "Aéroport Nice Côte d’Azur, à environ 1h20-1h30.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur privé pour La Croix-Valmer",
    subheading:
      "Des plages de Gigaro aux vignobles et au Cap Lardier, un service premium dans un golfe préservé.",
    paragraphs: [
      "La Croix-Valmer déroule un littoral préservé au sud du golfe de Saint-Tropez : les plages de Gigaro, les criques du Cap Lardier et le sentier du littoral côtoient les domaines viticoles des coteaux. En été, l’accès aux plages sature vite ; une dépose et une reprise au plus près évitent la recherche d’un stationnement rare.",
      "La commune n’a pas d’aéroport : l’arrivée se fait le plus souvent par Nice Côte d’Azur, à environ 1h20 à 1h30 de route selon le trafic estival. Nous calons l’heure de départ pour limiter l’attente, et proposons la gare TGV de Saint-Raphaël, à une trentaine de minutes, comme point de transfert alternatif.",
      "Pour la clientèle des villas et des domaines, un même chauffeur relie les plages de Gigaro, les vignobles et Saint-Tropez tout proche, avec discrétion et horaires souples. Forfaits fixes définis à l’avance ou mise à disposition à l’heure : devis immédiat.",
    ],
    bullets: [
      "Navettes plages de Gigaro",
      "Transfert aéroport de Nice & gare de Saint-Raphaël",
      "Vignobles & Cap Lardier",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à La Croix-Valmer",
    steps: [
      "Indiquez votre trajet (village, Gigaro, aéroport, gare) et vos horaires.",
      "Nous recommandons le véhicule et le forfait adaptés.",
      "Le chauffeur suit votre vol ou votre train et ajuste le départ.",
      "Transfert direct ou mise à disposition pour le golfe pendant votre séjour.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "La Croix-Valmer"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice depuis La Croix-Valmer ?",
      a: "Comptez environ 1h20 à 1h30 hors affluence, davantage lors des week-ends et pics de trafic estivaux du golfe. Nous adaptons l’heure de départ et suivons votre vol.",
    },
    {
      q: "Desservez-vous la gare TGV de Saint-Raphaël ?",
      a: "Oui, la gare de Saint-Raphaël se situe à environ 30 à 40 minutes de La Croix-Valmer ; c’est un point de transfert alternatif pratique pour les arrivées et départs en train.",
    },
    {
      q: "Assurez-vous la liaison avec Saint-Tropez ?",
      a: "Oui, Saint-Tropez est tout proche ; nous assurons les allers-retours vers le port, les restaurants et les soirées, avec retour de nuit.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "En haute saison estivale, réservez au plus tôt : la demande est forte dans le golfe de Saint-Tropez. Nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale et aux séjours en villa.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berline Mercedes, van premium (Classe V), SUV et minibus selon le nombre de passagers et les bagages, adaptés à la longue distance depuis Nice ou Cannes.",
    },
    {
      q: "Desservez-vous les plages de Gigaro ?",
      a: "Oui, nous assurons les allers-retours vers les plages de Gigaro et le départ du sentier du Cap Lardier, sans contrainte de stationnement et avec des horaires souples l’été.",
    },
  ],
  relatedLinks: [
    {
      label: "Voiture avec chauffeur à Saint-Tropez",
      href: "/chauffeur/saint-tropez",
    },
    { label: "Chauffeur privé Cannes", href: "/chauffeur/cannes" },
    {
      label: "Voiture avec chauffeur sur la Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Location avec livraison à Saint-Tropez",
      href: "/location/location-voiture-saint-tropez",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à La Croix-Valmer",
    text: "Transfert aéroport de Nice, gare de Saint-Raphaël ou navette vers Gigaro : réservez votre chauffeur, réponse rapide 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à La Croix-Valmer — VTC & transfert aéroport",
    description:
      "Chauffeur privé et VTC à La Croix-Valmer : transfert aéroport de Nice, gare de Saint-Raphaël, plages de Gigaro, vignobles et liaison Saint-Tropez.",
    canonical: "/chauffeur/la-croix-valmer",
  },
};

export default laCroixValmer;
